import { useCallback, useState } from "react";
import { Conversion } from "@jammable/clientapi";
import { Aside } from "./components/Aside";
import { useConversion } from "./hooks/useConversion";
import { Loader } from "./components/Loader";
import { Result, ResultProps } from "./components/Result";
import { getSubscriptionStatus } from "@jammable/ui-payments-feature/src/utils";
import { Header, HeaderProps } from "./components/Header";
import { Error } from "./components/Error";
import {
  AnotherVoice,
  BlockProps,
  CreateVideo,
  Lyrics,
  MakeYourOwn,
  RateConversion,
  VoiceList,
} from "./components/Aside/Blocks";
import { activeLineAt, joinArtistNames, useTranscription } from "./utils";

export interface ConversionPageProps {
  conversion: Conversion;
  signedInUser?: BlockProps["signedInUser"] & HeaderProps["signedInUser"];
  actionHandler?: BlockProps["actionHandler"] & HeaderProps["actionHandler"];
  isUK?: boolean;
}

export function ConversionPage({
  conversion,
  signedInUser,
  actionHandler,
  isUK,
}: ConversionPageProps) {
  const conversionData = useConversion(conversion);

  const { isComplete, isErrored, user, model } = conversionData;
  // Compared on the Mongo user id: the page no longer receives the owner's
  // firebaseUid (see apps/web/pages/api/model/getConversion.js).
  const ownerId = conversion?.user?._id;
  const isAuthor = Boolean(
    signedInUser?._id &&
      ownerId &&
      String(signedInUser._id) === String(ownerId),
  );

  const transcription = useTranscription(
    isComplete ? conversionData?.outputUrls?.transcription : null,
  );
  // Only the active lyric line is lifted out of the player; setState with
  // the same index bails out, so the page re-renders once per line, not
  // every player tick.
  const [activeLine, setActiveLine] = useState(-1);
  const onTimeUpdate = useCallback(
    (seconds: number) => setActiveLine(activeLineAt(transcription, seconds)),
    [transcription],
  );

  const headerProps = {
    ...(conversionData as unknown as HeaderProps),
    createdAt: (conversionData as any)?.createdAt,
    actionHandler,
    userId: ownerId,
    signedInUser,
    status: conversionData?.status,
  };

  const blockProps: BlockProps = {
    model: model!,
    user: user!,
    signedInUser,
    actionHandler,
    conversion: conversionData,
    isUK,
  };

  const voices =
    conversionData?.models && conversionData.models.length > 0
      ? conversionData.models
      : model
        ? [model as any]
        : [];
  const artistNames = joinArtistNames(model, conversionData?.models);

  if (isErrored) {
    return (
      <section className="tw-flex tw-flex-col tw-gap-6 tw-pb-16">
        <Header {...headerProps} variant="full" />
        <Error modelUrlParam={model?.urlParam ? model!.urlParam : "create"} />
      </section>
    );
  }

  if (!isComplete) {
    return (
      <section className="tw-flex tw-flex-col tw-gap-4 tw-pb-16">
        <Header {...headerProps} variant="compact" />
        <Loader
          status={conversionData?.status}
          progress={conversionData?.progress}
          stage={conversionData?.stage}
          models={voices}
          artistNames={artistNames}
          isAuthor={isAuthor}
          conversionId={conversion?._id}
          subscriptions={isAuthor ? signedInUser?.subscriptions : undefined}
        />
      </section>
    );
  }

  const lyrics = (
    <Lyrics transcription={transcription} activeLine={activeLine} />
  );

  if (!isAuthor) {
    return (
      <section className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1040px] tw-flex-col tw-gap-6 tw-pb-16 md:tw-gap-7">
        <Header {...headerProps} variant="hero">
          <Result
            {...(conversionData as ResultProps)}
            isUK={isUK}
            variant="visitor"
            onTimeUpdate={onTimeUpdate}
          />
        </Header>
        <MakeYourOwn {...blockProps} />
        <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">
          {lyrics}
          {/* No source-song card for visitors: a shared cover shows the
              voice, not the track it was made from. */}
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-4">
            <VoiceList {...blockProps} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="tw-grid tw-items-start tw-gap-6 tw-pb-16 lg:tw-grid-cols-[minmax(0,1fr)_380px] lg:tw-gap-7">
      <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-6">
        <Header {...headerProps} variant="full" />
        <Result
          {...(conversionData as ResultProps)}
          isUK={isUK}
          variant="owner"
          onTimeUpdate={onTimeUpdate}
          videoHref={
            conversionData?.status === "success"
              ? `/studio/${conversionData.shortid}`
              : undefined
          }
          actionsExtra={<RateConversion {...blockProps} />}
          hasPower={
            getSubscriptionStatus(signedInUser?.subscriptions) === "power"
          }
        />
        <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">
          {lyrics}
          {/* Hide CreateVideo for UK users or when video is already in progress */}
          {!isUK && !conversionData?.videoSettings?.progress && (
            <CreateVideo {...blockProps} />
          )}
        </div>
        <AnotherVoice {...blockProps} />
      </div>
      <Aside {...blockProps} />
    </section>
  );
}
